import type { SearchClient, SearchResponse } from 'algoliasearch';
import type { IndexUiState } from 'instantsearch.js';
import { Product_Family_Name_Enum, Product_Sub_Family_Name_Enum } from '@kheops/graphql';
import { PackagingFamilyTypeIntl, ProductFamilyTypeIntl } from '../../common/i18n/product-family.translation';
import type { DiscoveryView } from '../state';

export const FAMILY_LEVEL_ATTRIBUTES = ['familyLvl1', 'familyLvl2', 'familyLvl3', 'familyLvl4'] as const;

export type FamilyLevelAttribute = typeof FAMILY_LEVEL_ATTRIBUTES[number];

export interface FamilyFacet {
  code: string;
  count: number;
}

export interface FamilyLevelFacetQuery {
  indexName: string;
  facets: [FamilyLevelAttribute];
  maxValuesPerFacet: 100;
  hitsPerPage: 0;
  facetFilters?: [string];
}

export function familyLevelFacetQuery(indexName: string, parents: string[]): FamilyLevelFacetQuery | undefined {
  const level = parents.length;

  const query: FamilyLevelFacetQuery = {
    indexName,
    facets: [FAMILY_LEVEL_ATTRIBUTES[level]],
    maxValuesPerFacet: 100,
    hitsPerPage: 0,
  };

  if (level > 0) {
    query.facetFilters = [`${FAMILY_LEVEL_ATTRIBUTES[level - 1]}:${parents[level - 1]}`];
  }

  return query;
}

export function familyPathFromRefinementList(refinementList: Record<string, string[]> | undefined): string[] {
  const path: string[] = [];

  for (const attribute of FAMILY_LEVEL_ATTRIBUTES) {
    const value = refinementList?.[attribute]?.[0];

    if (value) {
      path.push(value);
    }
  }

  return path;
}

export function discoveryIndexName(view: DiscoveryView): string {
  switch (view) {
    case 'PRODUCTS':
      return import.meta.env.REACT_APP_ALGOLIA_PACKAGING_INDEX;
    case 'SUPPLIERS':
      return import.meta.env.REACT_APP_ALGOLIA_COMPANY_INDEX;
  }
}

export function familyLevelRefinementList(path: string[]): Record<FamilyLevelAttribute, string[]> {
  return {
    familyLvl1: path[0] ? [path[0]] : [],
    familyLvl2: path[1] ? [path[1]] : [],
    familyLvl3: path[2] ? [path[2]] : [],
    familyLvl4: path[3] ? [path[3]] : [],
  };
}

export function indexUiStateWithFamilyPath(indexUiState: IndexUiState | undefined, path: string[]): IndexUiState {
  return {
    ...indexUiState,
    refinementList: {
      ...indexUiState?.refinementList,
      ...familyLevelRefinementList(path),
    },
  };
}

export interface FamilyLevelRefinement {
  attribute: string;
  refinements: { value: string | number }[];
}

export interface FamilyLevelSelection {
  depth: number;
  value: string;
}

export function applyFamilyPath(
  setIndexUiState: (updater: (previous: IndexUiState) => IndexUiState) => void,
  path: string[],
): void {
  const familyLevels = familyLevelRefinementList(path);

  setIndexUiState((previous) => ({
    ...previous,
    page: 1,
    refinementList: {
      ...previous.refinementList,
      ...familyLevels,
    },
  }));
}

export function familyLevelLabelKey(code: string, depth: number): string {
  if (depth === 0 && code in ProductFamilyTypeIntl) {
    return `product-family:${ProductFamilyTypeIntl[code as Product_Family_Name_Enum]}`;
  }

  if (depth === 1 && code in PackagingFamilyTypeIntl) {
    return `product-family:${PackagingFamilyTypeIntl[code as Product_Sub_Family_Name_Enum]}`;
  }

  return `product-classification:${code}`;
}

export function familyFacetsForParent(facets: FamilyFacet[], childCodes: string[]): FamilyFacet[] {
  const children = new Set(childCodes);

  return facets.filter((facet) => children.has(facet.code));
}

export function sortFamilyFacets(items: FamilyFacet[], labelOf: (code: string) => string): FamilyFacet[] {
  return [...items].sort((a, b) => {
    const aOther = a.code.startsWith('AUTRE');
    const bOther = b.code.startsWith('AUTRE');

    //If one is other like "AUTRE_XXX", put it at the end of the list
    if (aOther !== bOther) {
      return aOther ? 1 : -1;
    }

    return labelOf(a.code).localeCompare(labelOf(b.code), 'fr');
  });
}

export async function fetchFamilyLevelFacets(
  searchClient: Pick<SearchClient, 'search'>,
  indexName: string,
  parents: string[],
  labelOf: (code: string) => string,
): Promise<FamilyFacet[]> {
  const query = familyLevelFacetQuery(indexName, parents);

  if (!query) {
    return [];
  }

  // Filters are not applied to the facets request because we want to get all the facets
  const { results } = await searchClient.search({ requests: [query] });
  const counts = (results[0] as SearchResponse | undefined)?.facets?.[query.facets[0]] ?? {};

  return sortFamilyFacets(
    Object.entries(counts).map(([code, count]) => ({ code, count })),
    labelOf,
  );
}
